/** @jsxImportSource @emotion/react */
import React, { useCallback, useEffect, useMemo } from 'react'
import { css } from '@emotion/react'
import { TextInput } from '../ts-components/TextInput'
import { TextAreaInput } from '../ts-components/TextAreaInput'
import { SelectInput } from '../ts-components/SelectInput'
import { NumberInput } from '../ts-components/NumberInput'
import DatePicker from '../ts-components/DatePicker'
import { DateOfBirthInput } from '../ts-components/DateOfBirthInput'
import ModernPhoneInput from '../ts-components/ModernPhoneInput'
import { CountrySelectInput } from '../ts-components/CountrySelectInput'
import { PlayerPositionSelect } from '../ts-components/PlayerPositionSelect'
import { Checkbox } from '../ts-components/Checkbox'
import Button from '../ts-components/Button'
import { FormGroup, FormItem } from '../../types/CustomForm'
import {
  filterEnabledGroups,
  sortGroupsByIndex,
  sortItemsByIndex,
  mapFormItemTypeToKind,
  extractOptions,
  relabelSelfRelationshipOption,
  extractNumberRange,
  extractMaxChar,
  formatFieldLabel,
  filterOptionByLabel,
  SEARCHABLE_OPTION_THRESHOLD,
} from '../../utils/customFormHelpers'
import {
  Container,
  SectionContainer,
  SectionHeader,
  SectionTitle,
  SectionSubtitle,
  QuestionsGrid,
  QuestionContainer,
  EmptyStateContainer,
  EmptyStateTitle,
  EmptyStateMessage,
  ButtonContainer,
} from './styles'
import { Dayjs } from 'dayjs'
import { toSnakeCase } from 'src/utils/stringUtils'
import { createTabIndexGenerator } from 'src/utils/formTabIndex'
import { handleFormErrors } from 'src/utils/scrollToError'
import HockeyUsaField from './HockeyUsaField'
import RichTextRenderer from '../RichTextRenderer'

// Answer actions
export type AnswerAction =
  | { type: 'SET'; id: string; value: FieldValue }
  | { type: 'SET_ALL'; payload: AnswerState }

interface DynamicFormProps {
  /** Array of form groups (sections) to render */
  groups: FormGroup[]
  /** Sport ID for player position selection */
  sportId?: string
  /** Handler for form submission with collected answers */
  onSubmit?: (answers: AnswerState) => void
  /** Handler for back button click */
  onBack?: () => void
  /** Loading state for buttons */
  loading?: boolean
  /** Custom text for back button */
  backButtonText?: string
  /** Custom text for next button */
  nextButtonText?: string
  /** Controlled form answers state */
  answers: AnswerState
  /** Dispatch function for answer updates */
  dispatchAnswer: (action: AnswerAction) => void
  /** Hide the bottom action buttons (useful when buttons are handled externally) */
  hideButtons?: boolean
  /** Ref to expose the submit function externally */
  submitRef?: React.MutableRefObject<(() => void) | null>
  /** Field errors */
  fieldErrors: any
  /** Custom error */
  customError: any
  /** Set of field IDs that should be disabled (e.g., pre-filled reserved fields) */
  disabledFields?: Set<string>
  /** Variant for styling special field components like HockeyUsaField */
  variant?: 'default' | 'modal'
  // scheduleId
  scheduleId?: string
  /** Optional: set of field IDs that were prefilled from API (used for read-only logic, e.g., Hockey USA) */
  prefilledIds?: Set<string>
}

// Answer state types - including arrays for checkbox/multi-select fields
export type FieldValue =
  | string
  | number
  | boolean
  | Dayjs
  | string[]
  | null
  | undefined
export type AnswerState = Record<string /* item.id */, FieldValue>

// Returns the placeholder to use for fields that support it
const getPlaceholder = (item: FormItem, kind: string) => {
  const displayName = item.display_label || item.name
  return item.description?.trim()
    ? item.description.trim() // use description when provided
    : `Enter ${displayName}${kind === 'textarea' ? '...' : ''}`
}

// Helper to determine if a field is required (handles 1/0, true/false, etc.)
function isFieldRequired(is_required: any): boolean {
  // Accepts 1, true, '1', 'true' as required
  return (
    is_required === 1 ||
    is_required === true ||
    is_required === '1' ||
    is_required === 'true'
  )
}

// Content section component - renders form content groups in full
const ContentSection: React.FC<{ content: string }> = ({ content }) => {
  return (
    <div css={SectionContainer}>
      {/* Top separator */}
      <div
        css={css`
          height: 1px;
          background: #33363b;
          margin: 0 0;
        `}
      />
      <RichTextRenderer content={content} showReadMore={false} />
      {/* Bottom separator */}
      <div
        css={css`
          height: 1px;
          background: #33363b;
          margin: 0 0;
        `}
      />
    </div>
  )
}

/**
 * DynamicForm component renders interactive custom form sections
 * Collects user input and provides form submission functionality
 * Uses the same rendering logic as DynamicFormPreview but with live interaction
 * Supports all field types including player position selection
 *
 * @param props - Component props containing form groups, sport ID, and handlers
 * @returns JSX element containing the rendered interactive form
 *
 * @example
 * ```tsx
 * <DynamicForm
 *   groups={selectedForm.groups}
 *   sportId={registration.sport_id}
 *   onSubmit={(answers) => console.log('Form submitted:', answers)}
 *   onBack={() => console.log('Back clicked')}
 *   loading={false}
 * />
 * ```
 */
const DynamicForm: React.FC<DynamicFormProps> = ({
  groups,
  sportId,
  onSubmit,
  onBack,
  loading = false,
  backButtonText = 'Back',
  nextButtonText = 'Next',
  answers,
  dispatchAnswer,
  hideButtons = false,
  submitRef,
  fieldErrors,
  customError,
  disabledFields,
  variant = 'default',
  scheduleId,
  prefilledIds,
}) => {
  const safeGroups = groups ?? []

  // Generate tab indices using closure to avoid re-renders
  // This maintains sequential order across renders without triggering React state updates
  const nextTabIndex = useMemo(() => createTabIndexGenerator(), [])

  // Effect to handle scrolling to errors when they occur
  useEffect(() => {
    const hasFieldErrors = fieldErrors && fieldErrors.length > 0
    const hasCustomErrors = customError && Object.keys(customError).length > 0

    if (hasFieldErrors || hasCustomErrors) {
      console.log('🔄 DynamicForm: Errors detected, triggering scroll', {
        fieldErrors,
        customError,
      })
      // Increase delay to ensure DOM is fully updated and prevent duplicate scrolls
      const scrollTimeout = setTimeout(() => {
        handleFormErrors(fieldErrors || [], customError || {}, false)
      }, 300)

      return () => clearTimeout(scrollTimeout)
    }
  }, [fieldErrors, customError])

  // Filter and sort groups and their items
  const enabledGroups = filterEnabledGroups(safeGroups)
  const sortedGroups = sortGroupsByIndex(enabledGroups).map(sortItemsByIndex)

  /**
   * 🔄 getFieldLabel: Gets the appropriate label for a field, including character limits for text-based fields
   */
  const getFieldLabel = (item: FormItem, kind: string): string => {
    // Show character limits for text-based fields that support maxChar
    const textBasedFields = ['text', 'email', 'url', 'textarea']

    if (textBasedFields.includes(kind)) {
      return formatFieldLabel(item)
    }

    // For other field types, use the standard label format
    return `${item.display_label || item.name}${
      isFieldRequired(item.is_required) ? ' *' : ''
    }`
  }

  /**
   * 🔄 renderField: Renders the appropriate input field based on form item type
   */
  const renderField = (
    item: FormItem,
    groupName: string,
    _fieldErrors: any,
    _customError: any
  ) => {
    const kind = mapFormItemTypeToKind(item.form_item_type.id)

    const commonProps = {
      name: `form.${toSnakeCase(groupName)}.${item.id}`,
      fieldErrors: _fieldErrors,
      customError: _customError,
      label: getFieldLabel(item, kind),
      disabled: disabledFields?.has(item.id) ?? false,
      tabIndex: nextTabIndex(),
    }

    switch (kind) {
      case 'text': {
        const maxChar = extractMaxChar(item)
        return (
          <div
            id={`field-${item.id}`}
            css={css`
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              gap: 8px;
              align-self: stretch;
              flex: 1;
            `}
          >
            <TextInput
              {...commonProps}
              value={String(answers[item.id] ?? '')}
              setValue={(v) =>
                dispatchAnswer({ type: 'SET', id: item.id, value: v })
              }
              placeholder={getPlaceholder(item, kind)}
              maxLength={maxChar || undefined}
            />
          </div>
        )
      }

      case 'number': {
        const { min, max } = extractNumberRange(item)
        const currentValue = answers[item.id]
        const stringValue =
          typeof currentValue === 'string' || typeof currentValue === 'number'
            ? String(currentValue)
            : ''
        return (
          <div
            id={`field-${item.id}`}
            css={css`
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              gap: 8px;
              align-self: stretch;
              flex: 1;
            `}
          >
            <NumberInput
              {...commonProps}
              value={stringValue}
              setValue={(v) =>
                dispatchAnswer({ type: 'SET', id: item.id, value: v })
              }
              min={min}
              max={max}
              placeholder={`Enter ${item.name}`}
            />
          </div>
        )
      }

      case 'player_number': {
        const currentValue = answers[item.id]
        const stringValue =
          typeof currentValue === 'string' || typeof currentValue === 'number'
            ? String(currentValue)
            : ''
        return (
          <div
            id={`field-${item.id}`}
            css={css`
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              gap: 8px;
              align-self: stretch;
              flex: 1;
            `}
          >
            <NumberInput
              {...commonProps}
              value={stringValue}
              setValue={(v) =>
                dispatchAnswer({ type: 'SET', id: item.id, value: v })
              }
              placeholder={`Enter ${item.name}`}
              max={999}
              min={1}
            />
          </div>
        )
      }

      case 'relationship':
      case 'teamOfficialRole':
      case 'dropdown': {
        // The relationship dropdown relabels the Self ("Me") option to
        // "Mark As Me"; all other dropdown kinds render options as-is.
        const options =
          kind === 'relationship'
            ? relabelSelfRelationshipOption(extractOptions(item))
            : extractOptions(item)
        // A pasted option list can run to hundreds of entries (Adray's 105
        // rinks), which is unusable as a plain scroll list. Short lists keep the
        // simpler no-search select.
        const isSearchable =
          kind === 'dropdown' && options.length >= SEARCHABLE_OPTION_THRESHOLD
        return (
          <div
            id={`field-${item.id}`}
            css={css`
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              gap: 8px;
              align-self: stretch;
              flex: 1;
            `}
          >
            <SelectInput
              {...commonProps}
              value={String(answers[item.id] ?? '')}
              onChange={(v) =>
                dispatchAnswer({ type: 'SET', id: item.id, value: v })
              }
              options={options}
              placeholder={`Select ${item.name}`}
              showSearch={isSearchable}
              filterOption={isSearchable ? filterOptionByLabel : undefined}
              notFoundContent={isSearchable ? 'No matching options' : undefined}
            />
          </div>
        )
      }

      case 'checkbox': {
        // Normalize the value to boolean: true for true, 1, '1'; false for false, 0, '0', undefined, null, ''
        const rawValue = answers[item.id]
        const checked = rawValue === true || rawValue === 1 || rawValue === '1'

        return (
          <div
            id={`field-${item.id}`}
            css={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '8px',
              width: '100%',
            }}
          >
            <Checkbox
              label={commonProps.label}
              name={commonProps.name}
              value={checked}
              onChange={(e) =>
                dispatchAnswer({
                  type: 'SET',
                  id: item.id,
                  value: e.target.checked,
                })
              }
              disabled={disabledFields?.has(item.id) ?? false}
              hint={item?.description ?? ''}
              fieldErrors={_fieldErrors}
              customError={_customError}
              tabIndex={commonProps.tabIndex}
              checkBoxStyle={css`
                width: auto;
                flex: 0 0 auto;
              `}
            />
          </div>
        )
      }

      case 'textarea': {
        const maxChar = extractMaxChar(item)
        return (
          <div
            id={`field-${item.id}`}
            css={css`
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              gap: 8px;
              align-self: stretch;
              flex: 1;
            `}
          >
            <TextAreaInput
              {...commonProps}
              value={String(answers[item.id] ?? '')}
              setValue={(v) =>
                dispatchAnswer({ type: 'SET', id: item.id, value: v })
              }
              placeholder={getPlaceholder(item, kind)}
              rows={3}
              maxLength={maxChar || undefined}
            />
          </div>
        )
      }

      case 'date':
        return (
          <div
            id={`field-${item.id}`}
            css={css`
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              gap: 8px;
              align-self: stretch;
              flex: 1;
            `}
          >
            <DatePicker
              label={commonProps.label}
              value={answers[item.id] as Dayjs | null}
              onChange={(date) =>
                dispatchAnswer({ type: 'SET', id: item.id, value: date })
              }
              tabIndex={commonProps.tabIndex}
            />
          </div>
        )

      case 'birthdate': {
        // DateOfBirthInput has 3 dropdowns, so we exclude tabIndex from commonProps
        const { tabIndex: _, ...dobProps } = commonProps
        return (
          <div
            id={`field-${item.id}`}
            css={css`
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              gap: 8px;
              align-self: stretch;
              flex: 1;
            `}
          >
            <DateOfBirthInput
              {...dobProps}
              value={String(answers[item.id] ?? '')}
              setValue={(v) =>
                dispatchAnswer({ type: 'SET', id: item.id, value: v })
              }
              // DateOfBirthInput has 3 dropdowns; assign tab indices in the
              // visual Day → Month → Year order (nextTabIndex increments per call).
              dayTabIndex={nextTabIndex()}
              monthTabIndex={nextTabIndex()}
              yearTabIndex={nextTabIndex()}
            />
          </div>
        )
      }

      case 'phone': {
        // Matches the phone field used in family management / edit player
        // profile: a single E.164 value (country calling code embedded). We do
        // not track the country separately — the submitted value is always the
        // final, complete number.
        return (
          <div
            id={`field-${item.id}`}
            css={css`
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              gap: 8px;
              align-self: stretch;
              flex: 1;
            `}
          >
            <ModernPhoneInput
              label={commonProps.label}
              value={String(answers[item.id] ?? '')}
              onChange={(v) =>
                dispatchAnswer({
                  type: 'SET',
                  id: item.id,
                  value: v.replace(/\s+/g, ''),
                })
              }
              error={customError[`form.${toSnakeCase(groupName)}.${item.id}`]}
              tabIndex={commonProps.tabIndex}
              disabled={disabledFields?.has(item.id) ?? false}
              validateOnBlur={false}
            />
          </div>
        )
      }

      case 'email': {
        const maxChar = extractMaxChar(item)
        return (
          <div
            id={`field-${item.id}`}
            css={css`
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              gap: 8px;
              align-self: stretch;
              flex: 1;
            `}
          >
            <TextInput
              {...commonProps}
              value={String(answers[item.id] ?? '')}
              setValue={(v) =>
                dispatchAnswer({ type: 'SET', id: item.id, value: v })
              }
              placeholder={`Enter ${item.display_label || item.name}`}
              type='email'
              maxLength={maxChar || undefined}
            />
          </div>
        )
      }

      case 'url': {
        const maxChar = extractMaxChar(item)
        return (
          <div
            id={`field-${item.id}`}
            css={css`
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              gap: 8px;
              align-self: stretch;
              flex: 1;
            `}
          >
            <TextInput
              {...commonProps}
              value={String(answers[item.id] ?? '')}
              setValue={(v) =>
                dispatchAnswer({ type: 'SET', id: item.id, value: v })
              }
              placeholder={`Enter ${item.name}`}
              type='url'
              maxLength={maxChar || undefined}
            />
          </div>
        )
      }

      case 'country':
        return (
          <div
            id={`field-${item.id}`}
            css={css`
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              gap: 8px;
              align-self: stretch;
              flex: 1;
            `}
          >
            <CountrySelectInput
              {...commonProps}
              value={String(answers[item.id] ?? '')}
              onChange={(v) =>
                dispatchAnswer({ type: 'SET', id: item.id, value: v || null })
              }
              placeholder={`Select ${item.name}`}
              tabIndex={commonProps.tabIndex}
            />
          </div>
        )

      case 'hockey_usa':
      case 'hockey_canada': {
        // Extract firstName and lastName from reserved fields
        const reservedFields = sortedGroups
          .flatMap((group) => group.items)
          .filter((field) => field.is_reserved)

        const firstNameField = reservedFields.find(
          (field) =>
            field.name.toLowerCase().includes('first name') ||
            field.name.toLowerCase().includes('firstname')
        )
        const lastNameField = reservedFields.find(
          (field) =>
            field.name.toLowerCase().includes('last name') ||
            field.name.toLowerCase().includes('lastname')
        )

        const firstName = firstNameField
          ? String(answers[firstNameField.id] ?? '')
          : ''
        const lastName = lastNameField
          ? String(answers[lastNameField.id] ?? '')
          : ''

        // Check if field should be read-only (already validated in details modal)
        // Prefer explicit prefilledIds when provided; fallback to current answers
        const isReadOnly =
          variant === 'modal' &&
          (prefilledIds ? prefilledIds.has(item.id) : !!answers[item.id])

        return (
          <div
            id={`field-${item.id}`}
            css={css`
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              gap: 8px;
              align-self: stretch;
              flex: 1;
            `}
          >
            <HockeyUsaField
              item={item}
              commonProps={commonProps}
              answers={answers}
              dispatchAnswer={dispatchAnswer}
              disabledFields={disabledFields}
              variant={variant}
              firstName={firstName}
              lastName={lastName}
              scheduleId={scheduleId}
              isReadOnly={isReadOnly}
              federation={kind === 'hockey_canada' ? 'canada' : 'usa'}
            />
          </div>
        )
      }

      case 'playerPosition': {
        // PlayerPositionSelect needs specific props, so we extract what we need
        const { disabled: _, ...playerPositionProps } = commonProps
        return (
          <div
            id={`field-${item.id}`}
            css={css`
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              gap: 8px;
              align-self: stretch;
              flex: 1;
            `}
          >
            <PlayerPositionSelect
              name={playerPositionProps.name}
              fieldErrors={playerPositionProps.fieldErrors}
              customError={playerPositionProps.customError}
              label={playerPositionProps.label}
              sportId={sportId}
              value={answers[item.id] as number | undefined}
              onChange={(v) =>
                dispatchAnswer({ type: 'SET', id: item.id, value: v ?? null })
              }
              disabled={disabledFields?.has(item.id) ?? false}
              tabIndex={playerPositionProps.tabIndex}
            />
          </div>
        )
      }

      default:
        return null
    }
  }

  /**
   * 🔄 handleSubmit: Handles form submission by calling onSubmit with collected answers
   */
  const handleSubmit = useCallback(() => {
    console.log('🔄 handleSubmit: Form submission triggered', { answers })
    if (onSubmit) {
      onSubmit(answers)
    }
  }, [answers, onSubmit])

  const isSubmitDisabled = loading

  // Expose submit function through ref
  useEffect(() => {
    if (submitRef) {
      submitRef.current = handleSubmit
    }
    return () => {
      if (submitRef) {
        submitRef.current = null
      }
    }
  }, [submitRef, handleSubmit])

  // Show empty state if no enabled groups
  if (sortedGroups.length === 0) {
    return (
      <div css={Container}>
        <div css={EmptyStateContainer}>
          <div css={EmptyStateTitle}>No Form Content</div>
          <div css={EmptyStateMessage}>
            This form has no enabled sections or questions to display.
          </div>
        </div>

        {/* Buttons for empty state */}
        {!hideButtons && (onBack || onSubmit) && (
          <div css={ButtonContainer}>
            {onBack && (
              <Button
                variant='secondary'
                text={backButtonText}
                disabled={loading}
                onClick={onBack}
              />
            )}
            {onSubmit && (
              <Button
                variant='primary'
                text={nextButtonText}
                disabled={loading}
                onClick={handleSubmit}
              />
            )}
          </div>
        )}
      </div>
    )
  }

  return (
    <div css={Container}>
      {sortedGroups.map((group) => {
        const isContentGroup = (group as any).is_content
        const content = (group as any).content

        if (isContentGroup && content) {
          // Render content section without header
          return <ContentSection key={group.id} content={content} />
        }

        // Render regular section with questions
        return (
          <div key={group.id} css={SectionContainer}>
            {/* Section Header */}
            <div css={SectionHeader}>
              <div css={SectionTitle}>{group.name}</div>
              {group.description && (
                <div css={SectionSubtitle}>{group.description}</div>
              )}
            </div>

            {/* Questions Grid */}
            <div css={QuestionsGrid}>
              {group.items.map((item) => {
                const fieldKind = mapFormItemTypeToKind(item.form_item_type.id)
                const isWideField = [
                  'textarea',
                  'checkbox',
                  'hockey_usa',
                  'hockey_canada',
                ].includes(fieldKind)

                return (
                  <div
                    key={item.id}
                    css={[
                      QuestionContainer,
                      isWideField && {
                        gridColumn: '1 / -1', // span across both columns
                      },
                    ]}
                  >
                    {renderField(
                      item,
                      group.name,
                      fieldErrors || [],
                      customError || {}
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )
      })}

      {/* Bottom Action Buttons */}
      {!hideButtons && (
        <div css={ButtonContainer}>
          <Button
            variant='secondary'
            text={backButtonText}
            disabled={loading}
            onClick={onBack || (() => {})}
          />
          <Button
            variant='primary'
            text={nextButtonText}
            disabled={isSubmitDisabled}
            onClick={handleSubmit}
          />
        </div>
      )}
    </div>
  )
}

export default DynamicForm
